import React, { useEffect, useState } from 'react'
import { Button, Form, Spinner } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { getAll, putOne } from '../../../../services/common_requests'

function extractStandardData(res: any): any {
  return res?.data ?? null
}

function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: any } })?.response?.data
  const message = data?.message ?? data?.payload?.message
  if (Array.isArray(message) && message.length) {
    return message.filter(Boolean).join(', ')
  }
  if (typeof message === 'string' && message.trim()) {
    return message.trim()
  }
  return fallback
}

const GoogleTagConfigTab: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [googleTagId, setGoogleTagId] = useState('')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await getAll({}, '/site-settings/legal')
        const d = extractStandardData(res)
        if (!cancelled) {
          setGoogleTagId(typeof d?.googleTagId === 'string' ? d.googleTagId : '')
        }
      } catch (e) {
        toast.error(
          apiErrorMessage(
            e,
            intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.ERROR_LOAD' }),
          ),
        )
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [intl])

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await putOne(
        { googleTagId: googleTagId.trim() },
        '/site-settings/legal',
      )
      if ((res as any)?.payload?.code === 3013) {
        toast.success(
          intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.SUCCESS_SAVE' }),
        )
      } else {
        toast.error(intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.ERROR_SAVE' }))
      }
    } catch (e) {
      toast.error(
        apiErrorMessage(
          e,
          intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.ERROR_SAVE' }),
        ),
      )
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className='text-muted d-flex align-items-center gap-2'>
        <Spinner animation='border' size='sm' />
        {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}
      </div>
    )
  }

  return (
    <div>
      <h3 className='fs-3 fw-bold text-gray-900 mb-2'>
        {intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG' })}
      </h3>
      <p className='text-muted mb-6'>
        {intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.DESCRIPTION' })}
      </p>

      <div className='card card-flush shadow-sm border border-primary border-dashed'>
        <div className='card-body py-6 px-6'>
          <Form.Group className='mb-0' style={{ maxWidth: 420 }}>
            <Form.Label className='fw-semibold text-gray-700'>
              {intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.ID' })}
            </Form.Label>
            <Form.Control
              className='form-control-solid form-control-lg'
              placeholder='AW-1010921229'
              value={googleTagId}
              onChange={(e) => setGoogleTagId(e.target.value)}
            />
            <div className='form-text'>
              {intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.HINT' })}
            </div>
          </Form.Group>

          <div className='mt-6'>
            <Button
              type='button'
              variant='primary'
              className='px-8'
              onClick={handleSave}
              disabled={saving}
            >
              {saving && <Spinner animation='border' size='sm' className='me-2' />}
              {saving
                ? intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.SAVING' })
                : intl.formatMessage({ id: 'TOUR_CONFIGURATIONS.GOOGLE_TAG.SAVE' })}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GoogleTagConfigTab
